import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Tag as TagIcon, Plug, Mail, Phone, Plus, Trash2, Pencil, MessageSquare, FolderOpen, Inbox as InboxIcon, Sparkles, AlertTriangle, CheckCircle2, ExternalLink, Upload, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { TIMEZONES } from "@/lib/timezones";
import { Badge, BadgeColorPicker, Button, Combobox, EmptyState, Input, Label, Skeleton, Textarea, useConfirm, useToast, Select, Modal, ModalFooter } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { uploadFile, makeUploadFilename } from "@/lib/upload";
import { DEFAULT_EMAIL_SUBJECTS } from "@/lib/email-subjects";
import { Avatar, STAFF_COLORS } from "@/components/ui/Avatar";
import { Timesheet } from "@/features/timeclock/Timesheet";
import { emailDomainOf, formatSender, syncsuiteEmailSettingsUrl, useEmailSending } from "@/lib/email-sending";
import type { CustomField, IntegrationsStatus, Tag, WorkspaceSettings } from "@/lib/types";

type TabKey = "workspace" | "fields" | "tags" | "integrations";

const TAB_KEYS: TabKey[] = ["workspace", "fields", "tags", "integrations"];

export function SettingsPage() {
  /* ?tab= so another screen can send somebody straight to the right one -
     checkout points at the card reader when none is paired. */
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab") as TabKey | null;
  const [tab, setTabState] = useState<TabKey>(asked && TAB_KEYS.includes(asked) ? asked : "workspace");
  const setTab = (k: TabKey) => {
    setTabState(k);
    setParams(k === "workspace" ? {} : { tab: k }, { replace: true });
  };
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Settings</h1>
        <p className="mt-1 text-sm text-zinc-500">Configure your workspace, fields, tags, and integrations.</p>
      </div>

      <div className="flex gap-1 border-b border-zinc-800 overflow-x-auto">
        {([
          { k: "workspace",    l: "Workspace",    Icon: Building2 },
          { k: "fields",       l: "Custom fields", Icon: TagIcon },
          { k: "tags",         l: "Tags",         Icon: TagIcon },
          { k: "integrations", l: "Integrations", Icon: Plug },
        ] as { k: TabKey; l: string; Icon: React.ComponentType<{ className?: string }> }[]).map(({ k, l, Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors " +
              (tab === k
                ? "border-indigo-500 text-zinc-100"
                : "border-transparent text-zinc-500 hover:text-zinc-300")
            }
          >
            <Icon className="h-4 w-4" />
            {l}
          </button>
        ))}
      </div>

      {tab === "workspace"    && <WorkspaceTab />}
      {tab === "fields"       && <FieldsTab />}
      {tab === "tags"         && <TagsTab />}
      {tab === "integrations" && <IntegrationsTab />}
    </div>
  );
}

/**
 * Her logo, uploaded once.
 *
 * Straight to public storage, because a mail client fetches the image as an
 * anonymous visitor - a private URL would arrive as a broken box in every
 * inbox.
 */
function LogoPicker({ value, onChange }: { value: string | null; onChange: (url: string | null) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const pick = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setBusy(true);
    try {
      const up = await uploadFile(file, {
        path: `branding/${makeUploadFilename(file.name, "logo")}`,
        bucket: "public",
      });
      onChange(up.publicUrl);
      toast.success("Logo uploaded - now press Save");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-white">
        {value
          ? <img src={value} alt="Logo" className="max-h-14 max-w-24 object-contain" />
          : <span className="text-[10px] text-zinc-400">No logo</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-200 hover:border-zinc-600">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {value ? "Replace" : "Upload a logo"}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={busy}
            onChange={(e) => { void pick(e.target.files?.[0] ?? null); e.target.value = ""; }}
          />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-zinc-400 hover:text-rose-300"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Workspace tab ─────────────────────────────────────────────────────────
function WorkspaceTab() {
  const toast = useToast();
  const [s, setS] = useState<WorkspaceSettings | null>(null);
  const [saving, setSaving] = useState(false);
  /**
   * The spa section's own save, handed up so the button at the bottom of the
   * page saves it too.
   *
   *   "I put the 3 in and it did not add to the purchases yesterday"
   *   "I see what I did wrong I only saved at the bottom not for the section"
   *
   * She typed her card fee, scrolled to the end, pressed the big Save changes
   * - and that button only ever saved the workspace's own fields, so the
   * number went nowhere and nothing said so. A page with one obvious Save at
   * the bottom has to mean it.
   */
  const saveSpaRef = useRef<null | (() => Promise<void>)>(null);

  useEffect(() => {
    api.get<{ settings: WorkspaceSettings }>("/settings")
      .then((d) => setS(d.settings))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));
  }, [toast]);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      // The spa settings first and on their own connection: that save merges
      // onto the LATEST stored block, so it must not be racing this one.
      await saveSpaRef.current?.();
      const res = await api.patch<{ settings: WorkspaceSettings }>("/settings", {
        workspace_name: s.workspace_name,
        logo_url: s.logo_url,
        timezone: s.timezone,
        default_from_email: s.default_from_email,
        default_from_name: s.default_from_name,
        default_phone: s.default_phone,
      });
      setS(res.settings);
      toast.success("Saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  if (!s) return <Skeleton className="h-64" />;

  return (
    <div className="grid grid-cols-1 gap-4 lg:max-w-3xl">
      <Section title="Branding">
        <div><Label>Workspace name</Label><Input value={s.workspace_name} onChange={(e) => setS({ ...s, workspace_name: e.target.value })} /></div>
        {/* "Like decorative up load photos logos stuff like that" - one
            upload here puts her logo at the top of every email campaign. */}
        <div className="mt-3">
          <Label>Logo</Label>
          <LogoPicker
            value={s.logo_url}
            onChange={(url) => setS({ ...s, logo_url: url })}
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            Goes at the top of every email campaign. A wide PNG with a transparent
            background looks best. Save after uploading.
          </p>
        </div>
        <div className="mt-3">
          <Label>Timezone</Label>
          <Combobox
            value={s.timezone}
            onChange={(v) => setS({ ...s, timezone: v })}
            options={(TIMEZONES.includes(s.timezone)
              ? TIMEZONES
              : [s.timezone, ...TIMEZONES]
            ).map((tz) => ({ value: tz }))}
            placeholder="America/Los_Angeles"
          />
        </div>
      </Section>
      <EmailSenderSection
        fromEmail={s.default_from_email}
        fromName={s.default_from_name}
        onChange={(patch) => setS({ ...s, ...patch })}
        savedAt={s.updated_at}
      />
      <BrandKitSection />
      <EmailLookSection />
      <Section title="Default phone">
        <div className="sm:max-w-xs">
          <Label>Outbound SMS / calls</Label>
          <Input type="tel" leftIcon={<Phone className="h-3.5 w-3.5" />} value={s.default_phone ?? ""} onChange={(e) => setS({ ...s, default_phone: e.target.value || null })} placeholder="+1…" />
        </div>
      </Section>
      <MedspaSettingsSection registerSave={(fn) => { saveSpaRef.current = fn; }} />
      <CardReaderSection />
      <StaffSection />
      {/* "can we add a time clock in for record and payroll." */}
      <Section title="Time clock">
        <p className="mb-3 text-xs text-zinc-500">
          Everybody clocks in and out from the front desk. This is what those hours add up
          to - the totals are what a pay run needs, and the shifts underneath are what you
          check when somebody disagrees. A shift corrected here keeps its original times
          and your name against the change.
        </p>
        <Timesheet />
      </Section>
      <div className="flex justify-end">
        <Button onClick={save} loading={saving}>Save changes</Button>
      </div>
    </div>
  );
}

/**
 * What every email looks like.
 *
 * "the subject line in the email can it be fixed to always be Heavenly
 * Hydration" and "I also need the Heavenly Hydration signature in the email.
 * I'll send to you because I added to the clients@ but it does not come up in
 * the software" — the mailbox's own signature belongs to the mailbox, so the
 * app needs its own copy.
 *
 * Self-contained, and merged server-side into settings.email, so saving here
 * can never wipe the scheduling block next door.
 */
/**
 * Her colours, her type and her pictures.
 *
 *   "Is there a way we can be able to select pictures fonts colors maybe
 *    upload our branding kit"
 *
 * The logo lives above with the workspace name; this is everything else. The
 * font list is deliberately short and plain: an email client will not download
 * a font, so the only honest options are families already on the reader's
 * machine. A brand font from a kit would render as something else on most
 * screens, which is worse than choosing a good one on purpose.
 */
function BrandKitSection() {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accent, setAccent] = useState("");
  const [heading, setHeading] = useState("sans");
  const [body, setBody] = useState("sans");
  const [fonts, setFonts] = useState<{ value: string; label: string }[]>([]);
  const [pictures, setPictures] = useState<{ id: string; url: string; name: string }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const d = await api.get<{
          brand: { accent_hex: string | null; heading_font: string; body_font: string };
          fonts: { value: string; label: string }[];
        }>("/brand");
        setAccent(d.brand.accent_hex ?? "");
        setHeading(d.brand.heading_font || "sans");
        setBody(d.brand.body_font || "sans");
        setFonts(d.fonts ?? []);
        const pics = await api.get<{ pictures: typeof pictures }>("/brand/pictures");
        setPictures(pics.pictures ?? []);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Couldn't load your brand");
      } finally { setLoaded(true); }
    })();
  }, [toast]);

  const save = async () => {
    const hex = accent.trim();
    if (hex && !/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) {
      toast.error("A colour looks like #6b3fa0");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/brand", {
        accent_hex: hex || null,
        heading_font: heading,
        body_font: body,
      });
      toast.success("Saved. Your next campaign uses it");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally { setSaving(false); }
  };

  const upload = async (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setUploading(true);
    try {
      const r = await uploadFile(file, {
        path: `mail/inline-images/${makeUploadFilename(file.name, "img")}`,
        bucket: "public",
      });
      const saved = await api.post<{ picture: { id: string; url: string; name: string } }>(
        "/brand/pictures", { url: r.publicUrl, name: file.name },
      );
      setPictures((prev) => [saved.picture, ...prev]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/brand/pictures/${id}`);
      setPictures((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove that");
    }
  };

  if (!loaded) return <Section title="Your brand"><Skeleton className="h-32" /></Section>;

  return (
    <Section title="Your brand">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <Label>Your colour</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(accent) ? accent : "#4f46e5"}
              onChange={(e) => setAccent(e.target.value)}
              className="h-9 w-10 shrink-0 cursor-pointer rounded border border-zinc-700 bg-transparent"
              aria-label="Pick your colour"
            />
            <Input value={accent} onChange={(e) => setAccent(e.target.value)} placeholder="#6b3fa0" />
          </div>
          <p className="mt-1 text-[11px] text-zinc-500">
            Headings, links and buttons in your emails. Leave it empty to use the preset.
          </p>
        </div>
        <div>
          <Label>Headings</Label>
          <Select value={heading} onChange={(e) => setHeading(e.target.value)}>
            {fonts.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </Select>
        </div>
        <div>
          <Label>Body text</Label>
          <Select value={body} onChange={(e) => setBody(e.target.value)}>
            {fonts.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </Select>
          <p className="mt-1 text-[11px] text-zinc-500">
            Email cannot carry a font file, so these are the ones already on every phone.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <Label>Your pictures</Label>
        <p className="mb-2 text-[11px] text-zinc-500">
          Upload once and pick them from the picture button while you are writing a campaign.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (f) await upload(f);
            e.target.value = "";
          }}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="grid h-20 w-20 shrink-0 place-items-center rounded-lg border border-dashed border-zinc-700 text-[11px] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 disabled:opacity-50"
          >
            {uploading ? "Uploading…" : "Add"}
          </button>
          {pictures.map((pic) => (
            <div key={pic.id} className="group relative h-20 w-20 overflow-hidden rounded-lg border border-zinc-800">
              <img src={pic.url} alt={pic.name} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => void remove(pic.id)}
                className="absolute right-0.5 top-0.5 hidden rounded bg-black/70 px-1 text-[10px] text-rose-200 group-hover:block"
                aria-label={`Remove ${pic.name}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Button onClick={save} loading={saving}>Save</Button>
      </div>
    </Section>
  );
}

function EmailLookSection() {
  const toast = useToast();
  const [subject, setSubject] = useState("");
  const [signature, setSignature] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get<{ settings: WorkspaceSettings }>("/settings")
      .then((d) => {
        const email = ((d.settings.settings as Record<string, unknown>)?.email ?? {}) as Record<string, unknown>;
        setSubject(typeof email.default_subject === "string" ? email.default_subject : "");
        setSignature(typeof email.signature_html === "string" ? email.signature_html : "");
        setLoaded(true);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { email: { default_subject: subject, signature_html: signature } });
      toast.success("Saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally { setSaving(false); }
  };

  if (!loaded) return <Section title="How your emails look"><Skeleton className="h-24" /></Section>;

  return (
    <Section title="How your emails look">
      <div className="sm:max-w-md">
        <Label>Subject line when nothing else sets one</Label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Heavenly Hydration"
        />
        <p className="mt-1 text-[11px] text-zinc-500">
          Replies and one-off emails go out with this. A message that names its own subject
          (a form to complete, an appointment confirmation) keeps that instead, because it tells
          the client more.
        </p>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between gap-2">
          <Label>Signature</Label>
          <button
            type="button"
            onClick={() => setPreview((v) => !v)}
            className="text-xs font-medium text-indigo-300 hover:text-indigo-200"
          >
            {preview ? "Edit" : "Preview"}
          </button>
        </div>
        {preview ? (
          <div
            data-signature-preview
            className="rounded-lg border border-zinc-800 bg-white p-4 text-black"
            dangerouslySetInnerHTML={{ __html: signature }}
          />
        ) : (
          <Textarea
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="<p><strong>Heavenly Hydration Medical Spa</strong><br>631-655-8920</p>"
            className="min-h-40 font-mono text-xs"
          />
        )}
        <p className="mt-1 text-[11px] text-zinc-500">
          Added under every email the app sends. Leave blank for none.
        </p>
      </div>
      <div className="mt-3 flex justify-end">
        <Button onClick={save} loading={saving} size="sm">Save email settings</Button>
      </div>
    </Section>
  );
}


/**
 * The card reader on the desk.
 *
 *   [13 Oct] "POS equipment ordered"
 *   [21 Oct] "Matt who sets up the card equipment I have"
 *
 * Two steps, in this order, because Stripe insists: somewhere for the reader
 * to live, then the reader. The pairing code is the three words the device
 * shows under Settings -> Generate pairing code, and it dies in a few
 * minutes, which is the single most common reason pairing fails.
 */
interface ReaderRow {
  id: string;
  label: string | null;
  device_type: string | null;
  status: string;
  busy: boolean;
}

function CardReaderSection() {
  const toast = useToast();
  const confirm = useConfirm();
  const [loaded, setLoaded] = useState(false);
  const [supported, setSupported] = useState(true);
  const [reason, setReason] = useState<string | null>(null);
  const [location, setLocation] = useState<{ id: string; display_name: string | null } | null>(null);
  const [readers, setReaders] = useState<ReaderRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("Front desk");
  const [addr, setAddr] = useState({ display_name: "", line1: "", line2: "", city: "", state: "", postal_code: "" });

  const load = () => {
    api.get<{ supported: boolean; reason?: string; location: { id: string; display_name: string | null } | null; readers: ReaderRow[] }>("/pos/terminal")
      .then((d) => {
        setSupported(d.supported);
        setReason(d.reason ?? null);
        setLocation(d.location);
        setReaders(d.readers ?? []);
      })
      .catch((err) => setReason(err instanceof ApiError ? err.message : "Couldn't reach the card processor"))
      .finally(() => setLoaded(true));
  };
  useEffect(load, []);

  const saveLocation = async () => {
    setBusy(true);
    try {
      await api.post("/pos/terminal/location", {
        display_name: addr.display_name.trim() || "The clinic",
        line1: addr.line1.trim(), line2: addr.line2.trim() || undefined,
        city: addr.city.trim(), state: addr.state.trim().toUpperCase(), postal_code: addr.postal_code.trim(),
      });
      toast.success("Address saved", "Now pair the reader below.");
      load();
    } catch (err) {
      toast.error("Stripe wouldn't take that address", err instanceof ApiError ? err.message : "Try again");
    } finally { setBusy(false); }
  };

  const pair = async () => {
    setBusy(true);
    try {
      await api.post("/pos/terminal/readers", { registration_code: code.trim(), label: label.trim() || undefined });
      setCode("");
      toast.success("Reader paired", "Charge on the reader now shows at checkout.");
      load();
    } catch (err) {
      toast.error("That didn't pair", err instanceof ApiError ? err.message : "Try a fresh code");
    } finally { setBusy(false); }
  };

  const unpair = async (r: ReaderRow) => {
    const ok = await confirm({
      title: `Unpair ${r.label ?? "this reader"}?`,
      description: "Checkout stops offering it. The device itself is untouched and can be paired again with a fresh code.",
      confirmLabel: "Unpair",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/pos/terminal/readers/${r.id}`);
      load();
    } catch (err) {
      toast.error("Couldn't unpair it", err instanceof ApiError ? err.message : "Try again");
    }
  };

  if (!loaded) return <Section title="Card reader"><Skeleton className="h-24" /></Section>;

  if (!supported) {
    return (
      <Section title="Card reader">
        <p className="text-sm text-zinc-400">{reason ?? "Connect card payments first, then a reader can be paired here."}</p>
      </Section>
    );
  }

  return (
    <Section title="Card reader">
      <p className="text-sm text-zinc-400">
        A Stripe Reader on the desk, so the amount appears on it when you press
        <span className="text-zinc-200"> Charge on the reader</span> at checkout. Nothing is typed twice.
      </p>

      {!location ? (
        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <p className="text-sm font-medium text-zinc-200">Step 1 - where the reader lives</p>
          <p className="mt-0.5 text-[11px] text-zinc-500">
            Stripe needs the clinic&rsquo;s address before it will pair a device. This is not shown to clients.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Name it</Label>
              <Input value={addr.display_name} onChange={(e) => setAddr({ ...addr, display_name: e.target.value })} placeholder="The clinic" />
            </div>
            <div className="sm:col-span-2">
              <Label>Street</Label>
              <Input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="123 Main St" />
            </div>
            <div className="sm:col-span-2">
              <Label>Suite (optional)</Label>
              <Input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
            </div>
            <div>
              <Label>Town</Label>
              <Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State</Label>
                <Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="NY" maxLength={2} />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} placeholder="11795" />
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" loading={busy} onClick={() => void saveLocation()}
              disabled={!addr.line1.trim() || !addr.city.trim() || addr.state.trim().length !== 2 || !addr.postal_code.trim()}>
              Save the address
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span>Readers live at <span className="text-zinc-200">{location.display_name ?? "the clinic"}</span>.</span>
          </div>

          {readers.length > 0 && (
            <ul className="divide-y divide-zinc-800/60 rounded-lg border border-zinc-800 bg-zinc-900/40">
              {readers.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-zinc-200">{r.label ?? "Card reader"}</p>
                    <p className="text-[11px] text-zinc-500">{r.device_type ?? "reader"}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge color={r.status === "online" ? "emerald" : "zinc"} size="xs">
                      {r.status === "online" ? (r.busy ? "Taking a card" : "Ready") : "Asleep"}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => void unpair(r)}>Unpair</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <p className="text-sm font-medium text-zinc-200">
              {readers.length ? "Pair another reader" : "Step 2 - pair the reader"}
            </p>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              On the reader: Settings &rarr; Generate pairing code. It shows three words. Type them here
              within a few minutes, before the code expires.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_12rem_auto] sm:items-end">
              <div>
                <Label>Pairing code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="quick-brown-fox" />
              </div>
              <div>
                <Label>Call it</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Front desk" />
              </div>
              <Button loading={busy} disabled={code.trim().length < 3} onClick={() => void pair()}>Pair it</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

/**
 * Med-spa scheduling & checkout knobs. Self-contained (own fetch + save)
 * because these live under settings.medspa in the workspace settings jsonb —
 * and the PATCH endpoint replaces the whole jsonb, so we re-fetch and merge
 * at save time to avoid clobbering keys other panels own.
 */
function MedspaSettingsSection({ registerSave }: { registerSave?: (fn: () => Promise<void>) => void }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [m, setM] = useState<Record<string, number | boolean | string>>({});

  const num = (v: unknown, dflt: number) => (typeof v === "number" && Number.isFinite(v) ? v : dflt);
  const str = (v: unknown) => (typeof v === "string" ? v : "");

  useEffect(() => {
    api.get<{ settings: WorkspaceSettings }>("/settings")
      .then((d) => {
        const ms = ((d.settings.settings as Record<string, unknown>)?.medspa ?? {}) as Record<string, unknown>;
        setM({
          tax_rate_pct: num(ms.tax_rate_pct, 0),
          slot_step_minutes: num(ms.slot_step_minutes, 30),
          min_notice_minutes: num(ms.min_notice_minutes, 60),
          max_advance_days: num(ms.max_advance_days, 60),
          cancellation_window_hours: num(ms.cancellation_window_hours, 24),
          reminder_lead_hours: num(ms.reminder_lead_hours, 72),
          second_reminder_lead_hours: num(ms.second_reminder_lead_hours, 24),
          virtual_link_lead_minutes: num(ms.virtual_link_lead_minutes, 15),
          followup_consult_months: num(ms.followup_consult_months, 6),
          followup_lab_months: num(ms.followup_lab_months, 3),
          insurance_verification_fee: num(ms.insurance_verification_fee, 25),
          insurance_claim_fee: num(ms.insurance_claim_fee, 5),
          insurance_short_year_fee: num(ms.insurance_short_year_fee, 10),
          insurance_min_claims: num(ms.insurance_min_claims, 3),
          insurance_after_deductible_pct: num(ms.insurance_after_deductible_pct, 10),
          reminders_enabled: ms.reminders_enabled !== false,
          notify_phone: str(ms.notify_phone),
          notify_phone_2: str(ms.notify_phone_2),
          notify_email: str(ms.notify_email),
          require_consult_first: ms.require_consult_first !== false,
          require_card_to_book: ms.require_card_to_book !== false,
          own_card_machine: ms.own_card_machine === true,
          card_fee_pct: num(ms.card_fee_pct, 0),
          google_review_url: str(ms.google_review_url),
          cancellation_policy_url: str(ms.cancellation_policy_url),
          website_url: str(ms.website_url),
          starts_per_slot: num(ms.starts_per_slot, 1),
          booking_note_new: str(ms.booking_note_new),
          booking_note_returning: str(ms.booking_note_returning),
          consult_note: str(ms.consult_note),
          email_subjects: str(ms.email_subjects) || DEFAULT_EMAIL_SUBJECTS.join("\n"),
        });
        setLoaded(true);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));
  }, [toast]);

  /**
   * What is on screen right now, readable from a closure that was made once.
   *
   * The page's Save at the bottom is handed this section's save a single time;
   * without these it would be handed the very first one, which closes over an
   * empty panel and would save nothing she typed.
   */
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;
  const mRef = useRef(m);
  mRef.current = m;

  const save = async (quiet = false) => {
    if (!loadedRef.current) return;
    setSaving(true);
    try {
      // Re-fetch so we merge onto the LATEST settings jsonb.
      const cur = await api.get<{ settings: WorkspaceSettings }>("/settings");
      // Merge INTO the stored medspa block so keys this panel doesn't show
      // survive a save.
      const curSettings = cur.settings.settings as Record<string, unknown>;
      const curMedspa = (curSettings?.medspa ?? {}) as Record<string, unknown>;
      const merged = { ...curSettings, medspa: { ...curMedspa, ...mRef.current } };
      await api.patch("/settings", { settings: merged });
      if (!quiet) toast.success("Spa settings saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
      throw err;
    } finally { setSaving(false); }
  };

  /* Handed to the page so its own Save at the bottom saves this too. `save`
     reads what is on screen through the refs above, so registering once is
     enough and it never goes stale. */
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    registerSave?.(() => saveRef.current(true));
    return () => registerSave?.(async () => {});
  }, [registerSave]);

  if (!loaded) return <Skeleton className="h-40" />;

  const numField = (key: string, label: string, hint?: string, step = 1) => (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        step={step}
        value={String(m[key] ?? 0)}
        onChange={(e) => setM({ ...m, [key]: parseFloat(e.target.value) || 0 })}
      />
      {hint && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );

  const textField = (key: string, label: string, placeholder?: string, hint?: string, type = "text") => (
    <div>
      <Label>{label}</Label>
      <Input
        type={type}
        value={typeof m[key] === "string" ? (m[key] as string) : ""}
        onChange={(e) => setM({ ...m, [key]: e.target.value })}
        placeholder={placeholder}
      />
      {hint && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );

  return (
    <Section title="Spa scheduling & checkout">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {numField("tax_rate_pct", "Sales tax %", "Applied to taxable services and all retail products at checkout.", 0.01)}
        {/* "I need the clients to be charged the cc fees I went to stripe and
            it has options to set this up not sure which is right" — none of
            them. The till writes the bill, so the fee goes on the bill, where
            she can see it before she presses and the client can see it on the
            receipt. */}
        {numField("card_fee_pct", "Card fee %", "What a client pays on top for paying by card. Checkout shows it before you press and the receipt names it. Cash never carries it, and neither do debit, HSA or FSA cards. 0 turns it off.", 0.01)}
        {numField("slot_step_minutes", "Booking time grid (minutes)", "Open times are offered every N minutes. 30 gives 8:00, 8:30, 9:00 and so on. Dragging a visit on the calendar snaps to the same grid.")}
        {numField("min_notice_minutes", "Minimum notice (minutes)", "How close to a start time clients can self-book online.")}
        {numField("max_advance_days", "Book up to (days ahead)")}
        {numField("cancellation_window_hours", "Cancellation window (hours)", "Your stated policy — shown to you when cancelling late.")}
        {numField("reminder_lead_hours", "First reminder (hours before)", "72 = three days out, which gives people time to move it. Nobody gets this one if they booked inside the window - it would just be the confirmation again.")}
        {numField("second_reminder_lead_hours", "Second reminder (hours before)", "The \u201clooking forward to seeing you tomorrow\u201d text, with your cancellation policy. Set 0 to turn it off.")}
        {numField("virtual_link_lead_minutes", "Video link (minutes before)", "How long before a video consultation the client is texted the join link. Set 0 to turn it off. The link is the provider's own room, set on their record.")}
        {/* "does the software put in the follow up consultation date 6 months
            from now?" / "can the date be automatically put in once the
            labwork is uploaded?" — both, and both are a number she owns. */}
        {numField("followup_consult_months", "Next consultation (months)", "When a consultation is marked done, the next one goes onto the client's chart this far out. Yours is 6. Which treatments count is a tick on each one, in Menu & seats.")}
        {numField("followup_lab_months", "Next lab work (months)", "When labs marked In House are uploaded, the next lab work goes onto the chart this far out, counted from the day the blood was drawn. Labs from the client's own doctor set nothing.")}
        {/* "I need a tracking for insurance … which for each service billed is
            a payout of 5.00 to the biller … if … the patient does not have at
            least three claims I owe 10 dollar pay out … after deductible is
            met our payout is 10 percent per charge" — her numbers, so they are
            hers to change when the arrangement does. */}
        {numField("insurance_verification_fee", "Insurance check (dollars)", "What a client pays you up front to have their policy checked. It is yours, not the biller's, and never counts toward a fee.", 0.01)}
        {numField("insurance_claim_fee", "Biller, per claim (dollars)", "What the biller earns on each claim while the client's deductible is still being met, including the claim that meets it.", 0.01)}
        {numField("insurance_after_deductible_pct", "Biller, after the deductible (%)", "Once the deductible is met, the biller takes this share of what the insurer actually pays, from the next claim on.")}
        {numField("insurance_min_claims", "Fewest claims a year", "Under this many claims in a calendar year, the short-year fee below is owed on top. A further claim takes it off again.")}
        {numField("insurance_short_year_fee", "Short-year fee (dollars)", "The flat amount owed when a client's year ends under that number of claims.", 0.01)}
        {textField("notify_phone", "Alert new website leads to (phone)", "+1…", "Where we ping you the moment someone submits your website form.", "tel")}
        {/* "can that same alarm split and come to my cell as well just to let
            me know?" — the same alert, a second time, to her. */}
        {textField("notify_phone_2", "Also send that alert to (phone)", "+1…", "A second phone that gets the same alert. Leave blank if the desk is enough.", "tel")}
        {textField("notify_email", "…or email", "you@yourspa.com", "Where we ping you the moment someone submits your website form.", "email")}
        {numField("starts_per_slot", "Online arrivals per time slot, per area", "How many clients online booking lets start at the same time in one part of the clinic (Sunroom, Lounge). 1 means the next online client is offered the following time. The front desk is never limited.")}
        {textField("google_review_url", "Google review link", "https://g.page/r/…/review", "In your Google Business Profile, click \u201cAsk for reviews\u201d and copy the link. Saved replies can drop it in with {{review_link}}.", "url")}
        {textField("cancellation_policy_url", "Cancellation policy link", "https://heavenlyhydrationli.com/policies", "Where clients can read your policy in full. Saved replies drop it in with {{cancellation_policy}}. It also shows on the card page, so they read it before handing the card over.", "url")}
        {textField("website_url", "Your website", "https://heavenlyhydrationli.com", "The way back for a client who has finished booking: their page offers a button to it. Point it at a thank-you page if you are tracking ads.", "url")}
      </div>
      {/* "can we have wording based on what they are booking or all in one
          part" — both. These two are the top of the page; each treatment can
          also carry its own line, on Manage → Menu & seats. */}
      <div className="mt-3">
        <Label>Note at the top of the booking page (first visit)</Label>
        <Input
          value={typeof m.booking_note_new === "string" ? (m.booking_note_new as string) : ""}
          onChange={(e) => setM({ ...m, booking_note_new: e.target.value })}
          placeholder="e.g. New with us? Allow a little extra time for your first visit."
        />
        <p className="mt-1 text-[11px] text-zinc-500">Shown to first-time clients above your treatment list, so they know what to pick.</p>
      </div>
      <div className="mt-3">
        <Label>Note at the top of the booking page (been here before)</Label>
        <Input
          value={typeof m.booking_note_returning === "string" ? (m.booking_note_returning as string) : ""}
          onChange={(e) => setM({ ...m, booking_note_returning: e.target.value })}
          placeholder="e.g. Welcome back. Your renewal consultation is due every 6 months."
        />
        <p className="mt-1 text-[11px] text-zinc-500">Shown to returning clients in the same place. Leave blank to say nothing.</p>
      </div>
      <div className="mt-3">
        {/* "the money price appears and that could be confusing so we need to
            write a blurb at the point of when they are choosing the
            consultation how it works" — and "Can I write something up here for
            these options?": yes, here. */}
        <Label>What to say when they are choosing a consultation</Label>
        <Textarea
          rows={4}
          value={typeof m.consult_note === "string" ? (m.consult_note as string) : ""}
          onChange={(e) => setM({ ...m, consult_note: e.target.value })}
          placeholder="e.g. Your first visit starts with a consultation. The fee shown is credited in full toward the treatment you have that day."
        />
        <p className="mt-1 text-[11px] text-zinc-500">
          Shown to a first-time client on the screen where they pick a consultation, which is where the price appears. Leave blank to use the first-visit note above.
        </p>
      </div>
      <div className="mt-3">
        <Label>Email subjects to offer</Label>
        {/* "can we get a drop down list for subject in emails" — one per line.
            They appear as suggestions under the Subject box; anything can
            still be typed over the top of them. */}
        <Textarea
          rows={4}
          value={typeof m.email_subjects === "string" ? (m.email_subjects as string) : ""}
          onChange={(e) => setM({ ...m, email_subjects: e.target.value })}
          placeholder={DEFAULT_EMAIL_SUBJECTS.join("\n")}
        />
        <p className="mt-1 text-[11px] text-zinc-500">
          One per line. These show as a list when you click the Subject box on an email, and the first one is the everyday default. You can still type any subject you like.
        </p>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={m.reminders_enabled !== false}
          onChange={(e) => setM({ ...m, reminders_enabled: e.target.checked })}
          className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        />
        Send automatic appointment reminders
      </label>
      <label className="mt-2 flex items-center gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={m.require_consult_first !== false}
          onChange={(e) => setM({ ...m, require_consult_first: e.target.checked })}
          className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        />
        Online, new clients can only book treatments marked &ldquo;New clients can book this online&rdquo; (IV drips and first consultations). Returning clients can book anything.
      </label>
      <label className="mt-2 flex items-start gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={m.require_card_to_book !== false}
          onChange={(e) => setM({ ...m, require_card_to_book: e.target.checked })}
          className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        />
        <span>
          Ask for a card to hold an online booking. The booking still goes through either
          way - the client is sent straight to the card page afterwards, and the visit shows
          &ldquo;No card&rdquo; on the board until one is on file.
        </span>
      </label>
      {/* "It says paid on the machine but it did not pay it." Checkout used to
          offer a card-machine button to everybody. It takes no money - it
          writes down that a machine of your own already did - so with no
          machine it just marked sales paid that nobody paid. Off unless you
          say there is one. */}
      <label className="mt-2 flex items-start gap-2 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={m.own_card_machine === true}
          onChange={(e) => setM({ ...m, own_card_machine: e.target.checked })}
          className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        />
        <span>
          I run cards on a card machine of my own. Checkout then offers an
          &ldquo;Already paid, card machine&rdquo; button, which writes the sale down as paid
          without charging anything - for money that has already gone through on that machine.
          Leave this off and the button stays hidden.
        </span>
      </label>
      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={() => void save()} loading={saving}>Save spa settings</Button>
      </div>
    </Section>
  );
}

/**
 * Email sender setup.
 *
 * The rule this panel exists to make obvious: SyncSuite only verifies the
 * DOMAIN of your sending address. Any mailbox on a verified domain works —
 * and your SyncSuite login email is irrelevant unless it happens to be on
 * one. Leave the field blank and sends use the workspace default.
 *
 * Verified domains are listed as one-click fills, and an address on an
 * unverified domain is flagged before the user finds out via a bounce.
 */
function EmailSenderSection({
  fromEmail,
  fromName,
  onChange,
  savedAt,
}: {
  fromEmail: string | null;
  fromName: string | null;
  onChange: (patch: { default_from_email?: string | null; default_from_name?: string | null }) => void;
  /** Changes on save — our cue to re-read the resolved effective sender. */
  savedAt: string;
}) {
  const { config, loading, refresh } = useEmailSending();
  const settingsUrl = syncsuiteEmailSettingsUrl();

  // Re-resolve after a save so "Emails will send as" reflects the value
  // just written rather than the one loaded on mount.
  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [savedAt]);

  const typedDomain = emailDomainOf(fromEmail);
  const verified = config?.known ? config.verified_domains : [];
  // Only claim a domain is unverified when we actually know the list.
  const domainOk = !config?.known || !typedDomain || verified.includes(typedDomain);

  return (
    <Section title="Email sender">
      <p className="text-xs text-zinc-400">
        Only the <span className="text-zinc-200">domain</span> has to be verified — any mailbox on it works
        (<span className="text-zinc-300">hello@</span>, <span className="text-zinc-300">news@</span>, …). This is
        independent of the email you log in with. Leave blank to use the workspace default.
      </p>

      {loading ? (
        <Skeleton className="mt-3 h-16" />
      ) : !config || !config.known ? null : verified.length === 0 ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">No verified sending domain — outbound email can't send yet.</p>
            <p className="mt-1 text-amber-200/80">
              Add your domain in SyncSuite, set the DNS records it gives you, and it'll appear here.
              {config.pending_domains.length > 0 && (
                <> Waiting on verification: {config.pending_domains.map((d) => d.hostname).join(", ")}.</>
              )}
            </p>
            {settingsUrl && (
              <a href={settingsUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 font-medium underline hover:text-amber-100">
                Open Email settings in SyncSuite <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Verified domains</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {verified.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onChange({ default_from_email: `${(fromEmail?.split("@")[0] || "info")}@${d}` })}
                title={`Use an address on ${d}`}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20"
              >
                <CheckCircle2 className="h-3 w-3" /> {d}
              </button>
            ))}
          </div>
          {config.effective.from && (
            <p className="mt-2.5 text-xs text-zinc-400">
              Emails will send as{" "}
              <span className="text-zinc-100">{formatSender(config.effective.from_name, config.effective.from)}</span>
              {config.effective.reply_to && <> · replies go to <span className="text-zinc-300">{config.effective.reply_to}</span></>}
            </p>
          )}
        </div>
      )}

      {/* A verified domain isn't enough on its own: SyncSuite refuses every
          send — personal and marketing alike — until the workspace's
          mailing address is on file. Personal email never displays it, so
          without this warning the only symptom is a failing send. */}
      {config?.known && !config.address_on_file && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">No business mailing address — outbound email can't send yet.</p>
            <p className="mt-1 text-amber-200/80">
              SyncSuite needs your business mailing address before any email goes out. It's never shown in
              personal email — US law only requires it in the footer of marketing email, where it's added
              automatically.
            </p>
            {settingsUrl && (
              <a href={settingsUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 font-medium underline hover:text-amber-100">
                Add it in SyncSuite <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label>From email</Label>
          <Input
            type="email"
            leftIcon={<Mail className="h-3.5 w-3.5" />}
            value={fromEmail ?? ""}
            onChange={(e) => onChange({ default_from_email: e.target.value || null })}
            placeholder={config?.platform_default_sender ?? "hello@yourdomain.com"}
          />
          {!domainOk && (
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-400">
              <AlertTriangle className="h-3 w-3" /> {typedDomain} isn't verified — sends from it will fail.
            </p>
          )}
        </div>
        <div>
          <Label>From name</Label>
          <Input
            value={fromName ?? ""}
            onChange={(e) => onChange({ default_from_name: e.target.value || null })}
            placeholder="Your business name"
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            Every email leaves under this name, whoever sent it. Leave it empty and
            each email goes out under the name of whoever is signed in.
          </p>
        </div>
      </div>
    </Section>
  );
}


// ─── Custom fields tab ─────────────────────────────────────────────────────
function FieldsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  // null = closed, "new" = create form, CustomField = edit form. Keeps the
  // dialog wired to a single state instead of separate open + target flags.
  const [editing, setEditing] = useState<CustomField | "new" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ fields: CustomField[] }>("/custom-fields");
      setFields(d.fields);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (f: CustomField) => {
    if (!(await confirm({
      title: `Delete field "${f.label}"?`,
      description: "Existing data on contacts is preserved — only the field definition is removed.",
      confirmLabel: "Delete field",
      danger: true,
    }))) return;
    try {
      await api.delete(`/custom-fields/${f.id}`);
      toast.success("Field deleted");
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  return (
    <div className="lg:max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-zinc-500 max-w-prose">Add custom data fields to your contacts. Ask AI to create them, or do it manually.</p>
        <Button leftIcon={<Plus className="h-4 w-4" />} size="sm" onClick={() => setEditing("new")} className="shrink-0">New field</Button>
      </div>
      {loading ? <Skeleton className="h-40" /> : fields.length === 0 ? (
        <EmptyState icon={TagIcon} title="No custom fields" description="Custom fields show up on contact records and can be used in lists, automations, and reports." />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {fields.map((f) => (
            <li key={f.id} className="flex items-start justify-between gap-3 border-b border-zinc-800/40 px-4 py-3 last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-100 truncate">{f.label}</p>
                <p className="text-xs text-zinc-500">
                  {FIELD_TYPE_LABELS[f.field_type] ?? f.field_type}
                  {f.required && " · required"}
                  {f.options.length > 0 && ` · ${f.options.length} options`}
                </p>
                {f.context && <p className="mt-1 text-xs text-zinc-400 italic">{f.context}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => setEditing(f)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                  aria-label="Edit"
                  title="Edit field"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(f)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-rose-500/10 hover:text-rose-400"
                  aria-label="Delete"
                  title="Delete field"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <FieldDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

// Field-type list with friendly labels + descriptions. Order matters — common
// types first, niche ones last.
const FIELD_TYPE_OPTIONS: { value: CustomField["field_type"]; label: string; hint: string }[] = [
  { value: "text",        label: "Short text",         hint: "A short answer (one line)" },
  { value: "longtext",    label: "Long text",          hint: "A multi-line note or paragraph" },
  { value: "number",      label: "Number",             hint: "Whole or decimal numbers" },
  { value: "boolean",     label: "Yes / No",           hint: "A simple checkbox — true or false" },
  { value: "dropdown",    label: "Dropdown",           hint: "Pick one from a list of options" },
  { value: "multiselect", label: "Multi-select",       hint: "Pick more than one from a list" },
  { value: "date",        label: "Date",               hint: "A specific calendar date" },
  { value: "datetime",    label: "Date & time",        hint: "A date with a specific time" },
  { value: "email",       label: "Email address",      hint: "Validates as an email" },
  { value: "phone",       label: "Phone number",       hint: "A phone number" },
  { value: "url",         label: "Website / URL",      hint: "A web link" },
];

/**
 * Unified create + edit dialog. `target` controls mode: "new" creates a
 * fresh field (with an auto-generated `key`), a CustomField instance puts
 * the dialog in edit mode. The auto-generated key is set on creation only
 * — automations and saved data continue to reference it after the user
 * renames the label, so the relationships stay intact.
 */
function FieldDialog({ target, onClose, onSaved }: {
  target: CustomField | "new" | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const open = target !== null;
  const editing = target && target !== "new" ? target : null;
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CustomField["field_type"]>("text");
  const [optionsText, setOptionsText] = useState("");
  const [context, setContext] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset every time the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setLabel(editing.label);
      setType(editing.field_type);
      setOptionsText(editing.options.join("\n"));
      setContext(editing.context ?? "");
    } else {
      setLabel(""); setType("text"); setOptionsText(""); setContext("");
    }
  }, [open, editing]);

  // The internal key is derived from the label only on create. Renaming
  // the label later does NOT change the key — that's what keeps existing
  // contact data + automation references stable.
  const key = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const typeOption = FIELD_TYPE_OPTIONS.find((t) => t.value === type)!;
  const contextOk = context.trim().length >= 10;

  const submit = async () => {
    if (!label.trim() || !contextOk) return;
    if (!editing && !key) return;
    setSaving(true);
    try {
      const body = {
        label: label.trim(),
        field_type: type,
        context: context.trim(),
        options: (type === "dropdown" || type === "multiselect")
          ? optionsText.split("\n").map((s) => s.trim()).filter(Boolean)
          : undefined,
      };
      if (editing) {
        await api.patch(`/custom-fields/${editing.id}`, body);
        toast.success("Field updated");
      } else {
        await api.post("/custom-fields", { key, ...body });
        toast.success("Field created");
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit custom field" : "Add a custom field"} size="md">
      <div className="space-y-4">
        <div>
          <Label required>What do you want to call this field?</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Annual Revenue, Birthday, Account Manager" autoFocus />
          <p className="mt-1.5 text-xs text-zinc-500">
            This is what you and your team will see on every contact.
            {editing && <> Renaming is safe — the field's underlying ID stays the same, so existing data and automations keep working.</>}
          </p>
        </div>

        <div>
          <Label required>What kind of information is this?</Label>
          <Select value={type} onChange={(e) => setType(e.target.value as CustomField["field_type"])}>
            {FIELD_TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <p className="mt-1.5 text-xs text-zinc-500">{typeOption.hint}</p>
        </div>

        {(type === "dropdown" || type === "multiselect") && (
          <div>
            <Label required>What are the choices? (one per line)</Label>
            <textarea
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
              placeholder={"Small\nMedium\nLarge"}
            />
          </div>
        )}

        <div>
          <Label required>What is this field for?</Label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
            placeholder="e.g. The customer's annual revenue. We use this to qualify them for our enterprise pricing tier."
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Helps the AI know when to read or write this field in automations and message templates.
            {context.length > 0 && context.length < 10 && (
              <span className="ml-1 text-amber-400">(at least 10 characters)</span>
            )}
          </p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!label.trim() || !contextOk || (!editing && !key)}>
          {editing ? "Save changes" : "Add field"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const FIELD_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  FIELD_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

// ─── Tags tab ──────────────────────────────────────────────────────────────
function TagsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  // null = closed, "new" = create form, Tag = edit form.
  const [editing, setEditing] = useState<Tag | "new" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ tags: Tag[] }>("/tags");
      setTags(d.tags);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (t: Tag) => {
    const usageCount = t.usage_count ?? 0;
    if (!(await confirm({
      title: `Delete tag "${t.name}"?`,
      description: usageCount > 0
        ? `This will remove the tag from ${usageCount} contact${usageCount === 1 ? "" : "s"}.`
        : "This tag isn't currently in use.",
      confirmLabel: "Delete tag",
      danger: true,
    }))) return;
    const before = tags;
    setTags((prev) => prev.filter((x) => x.id !== t.id));
    try {
      await api.delete(`/tags/${t.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      setTags(before);
    }
  };

  return (
    <div className="lg:max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-zinc-500 max-w-prose">Tags help organize contacts and trigger automations. Each tag includes context the AI reads to know when to apply or remove it.</p>
        <Button leftIcon={<Plus className="h-4 w-4" />} size="sm" onClick={() => setEditing("new")} className="shrink-0">New tag</Button>
      </div>
      {loading ? <Skeleton className="h-40" /> : tags.length === 0 ? (
        <EmptyState icon={TagIcon} title="No tags yet" description="Tags help organize contacts and trigger automations." action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Create your first tag</Button>} />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {tags.map((t) => (
            <li key={t.id} className="flex items-start justify-between gap-3 border-b border-zinc-800/40 px-4 py-3 last:border-0">
              <div className="flex flex-1 items-start gap-3 min-w-0">
                {/* Fixed-width badge column keeps all descriptions aligned
                    to the same left edge regardless of the tag's name length. */}
                <div className="w-32 shrink-0 pt-0.5">
                  <Badge color={t.color}>{t.emoji ? `${t.emoji} ` : ""}{t.name}</Badge>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-zinc-500">{t.usage_count ?? 0} contacts</p>
                  {t.context && <p className="mt-1 text-xs text-zinc-400 italic">{t.context}</p>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => setEditing(t)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                  aria-label="Edit"
                  title="Edit tag"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(t)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-rose-500/10 hover:text-rose-400"
                  aria-label="Delete"
                  title="Delete tag"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <TagDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={(t) => {
          setEditing(null);
          // Optimistic local merge for create; for edit we just trust the
          // returned row to be canonical. Either way reload so usage_count
          // and any backend-side changes show up.
          if (t) setTags((prev) => {
            const idx = prev.findIndex((x) => x.id === t.id);
            if (idx === -1) return [...prev, { ...t, usage_count: 0 }];
            const next = [...prev]; next[idx] = { ...t, usage_count: prev[idx]!.usage_count }; return next;
          });
          load();
        }}
      />
    </div>
  );
}

/**
 * Unified create + edit dialog for tags. Editing the name only changes
 * the display label — contact-tag relationships are stored by tag ID, so
 * existing tagged contacts and automations referencing the tag's row keep
 * working. (Smart-list rules that reference the tag by name will need to
 * be updated after a rename, since those store the literal name.)
 */
function TagDialog({ target, onClose, onSaved }: {
  target: Tag | "new" | null;
  onClose: () => void;
  onSaved: (t: Tag | null) => void;
}) {
  const toast = useToast();
  const open = target !== null;
  const editing = target && target !== "new" ? target : null;
  const [name, setName] = useState("");
  const [color, setColor] = useState("indigo");
  const [emoji, setEmoji] = useState("");
  const [context, setContext] = useState("");
  const [saving, setSaving] = useState(false);
  const contextOk = context.trim().length >= 10;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setColor(editing.color);
      setEmoji(editing.emoji ?? "");
      setContext(editing.context ?? "");
    } else {
      setName(""); setColor("indigo"); setEmoji(""); setContext("");
    }
  }, [open, editing]);

  const submit = async () => {
    if (!name.trim() || !contextOk) return;
    setSaving(true);
    try {
      if (editing) {
        const r = await api.patch<{ tag: Tag }>(`/tags/${editing.id}`, {
          name: name.trim(), color, emoji: emoji.trim(), context: context.trim(),
        });
        toast.success("Tag updated");
        onSaved(r.tag);
      } else {
        const r = await api.post<{ tag: Tag }>("/tags", {
          name: name.trim(), color, emoji: emoji.trim(), context: context.trim(),
        });
        toast.success("Tag created");
        onSaved(r.tag);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit tag" : "Add a tag"} size="md">
      <div className="space-y-4">
        <div>
          <Label required>Tag name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP, Hot Lead, Customer" autoFocus />
          {editing && (
            <p className="mt-1.5 text-xs text-zinc-500">
              Renaming the tag is safe — your tagged contacts and automation triggers stay linked by tag ID.
            </p>
          )}
        </div>
        <div>
          <Label>Color</Label>
          <BadgeColorPicker value={color} onChange={setColor} />
        </div>
        <div>
          <Label>Emoji</Label>
          <Input
            value={emoji}
            onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
            placeholder="e.g. 🎁 🥑 ⚠️"
            className="w-28"
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Worn beside the client's name everywhere — the front desk, the calendar, their chart.
          </p>
        </div>
        <div>
          <Label required>When should this tag be used?</Label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
            placeholder="e.g. Apply when a lead has replied within 24 hours and asked about pricing. Remove when they convert to a customer or go cold for 30+ days."
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Helps the AI know when to apply or remove this tag in automations and smart lists.
            {context.length > 0 && context.length < 10 && (
              <span className="ml-1 text-amber-400">(at least 10 characters)</span>
            )}
          </p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim() || !contextOk}>
          {editing ? "Save changes" : "Add tag"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Plain-English status panel. The underlying integrations are technical
 * (Postgres / Redis / API keys / webhook secrets) but the user shouldn't
 * need to know any of that — they just need to know whether things work
 * and what to do if they don't. We bucket the raw flags into four
 * everyday capabilities and only escalate the technical detail when
 * something's actually broken.
 */
function IntegrationsTab() {
  const toast = useToast();
  const [status, setStatus] = useState<IntegrationsStatus | null>(null);

  useEffect(() => {
    api.get<{ integrations: IntegrationsStatus }>("/settings")
      .then((d) => setStatus(d.integrations))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));
  }, [toast]);

  if (!status) return <Skeleton className="h-64" />;

  // Plain-English buckets the user actually cares about. Each one rolls
  // up the technical flags it depends on so we can show one row instead
  // of five. Order matters — the foundational pieces (data + background
  // automations) come first so the user reads them before the
  // messaging-specific rows.
  const items: { title: string; explainer: string; ok: boolean; Icon: React.ComponentType<{ className?: string }>; details?: string }[] = [
    {
      title: "Saving your data",
      explainer: "Stores your contacts, messages, tasks, notes, and everything else the CRM keeps track of.",
      ok: status.database,
      Icon: FolderOpen,
      details: status.database ? undefined : "The CRM can't reach its data storage right now. Ask your AI to check it.",
    },
    {
      title: "Background automations",
      explainer: "Runs automations, bulk imports, and scheduled tasks so things happen even when you're not looking at the CRM.",
      ok: status.redis,
      Icon: Sparkles,
      details: status.redis ? undefined : "Background automations aren't running, so automations and bulk actions might be slower or skipped.",
    },
    {
      title: "Sending messages",
      explainer: "Lets the CRM send emails, text messages, and social DMs through your SyncSuite account.",
      ok: status.services_api,
      Icon: MessageSquare,
      details: status.services_api ? undefined : "SyncSuite isn't connected yet — without it, outbound email, SMS, and social won't go anywhere. Ask your AI to link it.",
    },
    {
      title: "Receiving messages",
      explainer: "Catches incoming texts, emails, and social DMs and drops them into your inbox.",
      ok: status.inbound_webhook_secret && status.mothership_url,
      Icon: InboxIcon,
      details: (status.inbound_webhook_secret && status.mothership_url)
        ? undefined
        : "Inbound delivery isn't fully wired up. New messages from your customers may not show up until this is fixed.",
    },
  ];

  const allGood = items.every((i) => i.ok);

  return (
    <div className="lg:max-w-3xl space-y-3">
      <div className={cn(
        "flex items-start gap-3 rounded-xl border p-4",
        allGood ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5",
      )}>
        <div className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
          allGood ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
        )}>
          <Plug className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-100">
            {allGood ? "Everything looks good" : "Something needs attention"}
          </p>
          <p className="mt-0.5 text-xs text-zinc-400">
            {allGood
              ? "Your CRM is fully set up and ready to go."
              : "Below are the things your CRM needs in order to work. If anything says \"Needs setup\", ask your AI to fix it — you don't have to figure out the technical side."}
          </p>
        </div>
      </div>

      {/* Divider + section heading so the summary card above doesn't blur
          into the per-capability list below. */}
      <div className="flex items-center gap-3 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          What your CRM needs
        </span>
        <div className="h-px flex-1 bg-zinc-800/70" />
      </div>

      {items.map((it) => <CapabilityRow key={it.title} {...it} />)}
    </div>
  );
}

function CapabilityRow({
  title, explainer, ok, Icon, details,
}: {
  title: string;
  explainer: string;
  ok: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  details?: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
      <div className={cn(
        "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
        ok ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
      )}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-zinc-100">{title}</p>
          <Badge color={ok ? "emerald" : "amber"} size="xs">{ok ? "Working" : "Needs setup"}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-zinc-400">{explainer}</p>
        {details && (
          <p className="mt-1 text-xs text-amber-300">{details}</p>
        )}
      </div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      {children}
    </div>
  );
}

// ─── Staff and what they can see ───────────────────────────────────────────
//
// She adds people herself: a new receptionist starts on Monday and shouldn't
// need a call to Matthew. And "everyone is an admin" is wrong for a medical
// practice — the front desk has no business in a chart, and the nurses have
// no business in the revenue reports.

interface StaffMember {
  id: string; name: string; email: string; staff_role: string;
  active: boolean; credential: string | null; color: string | null; provider_id: string | null;
  provider_name: string | null; is_medical_director: boolean;
  last_seen_at: string | null;
  /** What they can open right now (their own list, or their role's). */
  capabilities: string[];
  custom_permissions: boolean;
}
interface RoleDef { key: string; label: string; blurb: string }
interface SectionDef { key: string; label: string; blurb: string }

export function StaffSection() {
  const toast = useToast();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [providers, setProviders] = useState<{ id: string; name: string; title: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newPassword, setNewPassword] = useState<{
    who: string; password: string;
    /** null when nothing was meant to be sent (a password reset). */
    emailed?: boolean | null; emailError?: string | null;
  } | null>(null);
  const [sections, setSections] = useState<SectionDef[]>([]);
  const [roleDefaults, setRoleDefaults] = useState<Record<string, string[]>>({});
  const [editingSections, setEditingSections] = useState<StaffMember | null>(null);

  const load = () => {
    setLoading(true);
    api.get<{ staff: StaffMember[]; roles: RoleDef[]; sections: SectionDef[]; role_defaults: Record<string, string[]> }>("/staff")
      .then((d) => { setStaff(d.staff); setRoles(d.roles); setSections(d.sections ?? []); setRoleDefaults(d.role_defaults ?? {}); })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) setDenied(true);
        else toast.error("Couldn't load the team");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get<{ providers: typeof providers }>("/providers")
      .then((d) => setProviders(d.providers)).catch(() => {});
  }, []);

  if (denied) return null;   // Not an owner — the section simply isn't there.

  const update = async (id: string, patch: Record<string, unknown>) => {
    try {
      await api.patch(`/staff/${id}`, patch);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that");
    }
  };

  const resetPassword = async (m: StaffMember) => {
    try {
      const d = await api.post<{ temporary_password: string | null }>(`/staff/${m.id}/password`, {});
      if (d.temporary_password) setNewPassword({ who: m.name, password: d.temporary_password });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't reset that password");
    }
  };

  return (
    <Section title="Staff and logins">
      <p className="mb-3 text-xs text-zinc-500">
        Everyone who works here signs in with their own email and password. Their role
        sets what they can see to start with (the front desk can book and take payment
        but can&rsquo;t open clinical notes). Click &ldquo;Sections&rdquo; to choose exactly which
        parts of the system each person can open.
      </p>

      {loading ? <Skeleton className="h-40" /> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800/70">
          <table className="w-full min-w-max text-sm">
            <thead className="bg-zinc-900/50 text-zinc-500">
              <tr className="text-left text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Can see</th>
                <th className="px-3 py-2 font-medium">On the calendar as</th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {staff.map((m) => (
                <tr key={m.id} className="border-t border-zinc-800/40">
                  <td className="px-3 py-2">
                    {/* "maybe we can assign colors to the staff" — her colour
                        is the avatar, and clicking it is how you change it. */}
                    <span className="mr-2 inline-flex align-middle">
                      <StaffColorPicker
                        name={m.name}
                        value={m.color}
                        onPick={(c) => update(m.id, { color: c })}
                      />
                    </span>
                    <span className="font-medium text-zinc-100">{m.name}</span>
                    {m.credential && <span className="ml-1 text-zinc-500">{m.credential}</span>}
                    {m.is_medical_director && (
                      <Badge color="violet" size="xs" className="ml-2">Medical director</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-400">{m.email}</td>
                  <td className="px-3 py-2">
                    <Select value={m.staff_role} className="h-7 w-36 text-xs"
                      onChange={(e) => update(m.id, { staff_role: e.target.value })}>
                      {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    {m.staff_role === "owner" ? (
                      <span className="text-xs text-zinc-500">Everything</span>
                    ) : (
                      <button
                        onClick={() => setEditingSections(m)}
                        className="rounded-md border border-zinc-800 px-2 py-1 text-[11px] text-zinc-200 hover:border-zinc-600"
                      >
                        Sections: {m.capabilities.filter((c) => sections.some((sec) => sec.key === c)).length} of {sections.length}
                        {m.custom_permissions && <span className="ml-1 text-indigo-300">(custom)</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Select value={m.provider_id ?? ""} className="h-7 w-40 text-xs"
                      onChange={(e) => update(m.id, { provider_id: e.target.value || null })}>
                      <option value="">Not on the calendar</option>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}{p.title ? ` ${p.title}` : ""}</option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => update(m.id, { active: !m.active })}
                      className={cn("rounded px-2 py-0.5 text-[11px] font-medium",
                        m.active ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-500")}>
                      {m.active ? "Active" : "Switched off"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => resetPassword(m)}
                      className="rounded-md px-2 py-1 text-[11px] text-indigo-300 hover:bg-zinc-800">
                      New password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <ul className="space-y-0.5 text-[11px] text-zinc-500">
          {roles.map((r) => (
            <li key={r.key}><span className="text-zinc-400">{r.label}</span> — {r.blurb}</li>
          ))}
        </ul>
        <Button size="sm" onClick={() => setAdding(true)}>Add someone</Button>
      </div>

      {adding && (
        <AddStaff roles={roles} providers={providers}
          onClose={() => setAdding(false)}
          onAdded={(who, pw, emailed, emailError) => {
            setAdding(false); load();
            if (pw) setNewPassword({ who, password: pw, emailed, emailError });
          }} />
      )}

      {editingSections && (
        <SectionsModal
          member={editingSections}
          sections={sections}
          roleLabel={roles.find((r) => r.key === editingSections.staff_role)?.label ?? editingSections.staff_role}
          roleDefault={roleDefaults[editingSections.staff_role] ?? []}
          onClose={() => setEditingSections(null)}
          onSave={async (permissions) => {
            await update(editingSections.id, { permissions });
            setEditingSections(null);
          }}
        />
      )}

      {newPassword && (
        <Modal open onClose={() => setNewPassword(null)} title="Their password">
          <p className="text-sm text-zinc-300">
            {newPassword.emailed
              ? <>Emailed to <span className="font-semibold text-zinc-100">{newPassword.who}</span>. Here it is as well, in case they ask - it won&rsquo;t be shown again.</>
              : <>Give this to <span className="font-semibold text-zinc-100">{newPassword.who}</span>. It won&rsquo;t be shown again.</>}
          </p>
          {newPassword.emailed === false && (
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              The email didn&rsquo;t go{newPassword.emailError ? `: ${newPassword.emailError}` : ""}. Their login works -
              you just need to pass this on yourself.
            </p>
          )}
          <p className="mt-3 select-all rounded-lg bg-zinc-800 px-3 py-2 text-center font-mono text-lg text-zinc-100">
            {newPassword.password}
          </p>
          <ModalFooter>
            <Button onClick={() => setNewPassword(null)}>Done</Button>
          </ModalFooter>
        </Modal>
      )}
    </Section>
  );
}

/**
 * "Will I be able to choose what they can see, by each section?" One box
 * per section; the role only supplies the starting ticks. Saving exactly the
 * role's set stores nothing custom, so a later role change still moves them.
 */
function SectionsModal({
  member, sections, roleLabel, roleDefault, onClose, onSave,
}: {
  member: StaffMember;
  sections: SectionDef[];
  roleLabel: string;
  roleDefault: string[];
  onClose: () => void;
  onSave: (permissions: string[] | null) => Promise<void>;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(member.capabilities));
  const [saving, setSaving] = useState(false);
  const toggle = (k: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const sameAsRole =
    sections.every((sec) => picked.has(sec.key) === roleDefault.includes(sec.key));

  return (
    <Modal open onClose={onClose} title={`What ${member.name.split(" ")[0]} can see`}>
      <div className="space-y-1">
        {sections.map((sec) => (
          <label key={sec.key} className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-zinc-900">
            <input
              type="checkbox"
              checked={picked.has(sec.key)}
              onChange={() => toggle(sec.key)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-500"
            />
            <span className="min-w-0">
              <span className="block text-sm text-zinc-100">{sec.label}</span>
              <span className="block text-xs text-zinc-500">{sec.blurb}</span>
            </span>
          </label>
        ))}
      </div>
      <p className="mt-3 text-xs text-zinc-500">
        {sameAsRole
          ? `These are the usual sections for ${roleLabel}.`
          : `Different from the usual ${roleLabel} sections.`}{" "}
        Takes effect straight away; their menu updates the next time they refresh or sign in.
      </p>
      <ModalFooter>
        {!sameAsRole && (
          <Button variant="ghost" className="mr-auto" onClick={() => setPicked(new Set(roleDefault))}>
            Reset to {roleLabel}
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          loading={saving}
          onClick={async () => {
            setSaving(true);
            try { await onSave(sameAsRole ? null : [...picked]); } finally { setSaving(false); }
          }}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function AddStaff({
  roles, providers, onClose, onAdded,
}: {
  roles: RoleDef[];
  providers: { id: string; name: string; title: string | null }[];
  onClose: () => void;
  onAdded: (who: string, password: string | null, emailed: boolean | null, emailError: string | null) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("front_desk");
  const [credential, setCredential] = useState("");
  const [providerId, setProviderId] = useState("");
  // "did they all get emails to log into our create account?" — now they do,
  // unless she'd rather hand the password over herself.
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const d = await api.post<{
        temporary_password: string | null; emailed: boolean | null; email_error: string | null;
      }>("/staff", {
        name: name.trim(), email: email.trim(), staff_role: role,
        credential: credential.trim() || null,
        provider_id: providerId || null,
        send_email: sendEmail,
      });
      onAdded(name.trim(), d.temporary_password, d.emailed, d.email_error);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that person");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Add someone to the team">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Their name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Miranda Schultz" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Email they'll sign in with</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">What they can do</label>
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </Select>
          <p className="mt-1 text-[11px] text-zinc-500">{roles.find((r) => r.key === role)?.blurb}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Letters after their name</label>
            <Input value={credential} onChange={(e) => setCredential(e.target.value)} placeholder="RN, LPN, PA-C" />
            <p className="mt-1 text-[10px] text-zinc-500">Stamped on every note they write.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">On the calendar as</label>
            <Select value={providerId} onChange={(e) => setProviderId(e.target.value)}>
              <option value="">Not on the calendar</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          />
          <span>
            Email them their sign-in details
            <span className="mt-0.5 block text-[11px] text-zinc-500">
              Their address, a temporary password and a link. Untick if you&rsquo;d rather hand
              it over yourself - you&rsquo;ll still see the password once either way.
            </span>
          </span>
        </label>
        <p className="text-[11px] text-zinc-500">
          A password will be generated and shown once — write it down and hand it over.
        </p>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy || !name.trim() || !email.trim()}>Add them</Button>
      </ModalFooter>
    </Modal>
  );
}


/**
 * Pick somebody's colour.
 *
 * "Maybe the colors that are specific to there sign in color like Miranda is
 * lavender. Oh I'm lavender too maybe we can assign colors to the staff."
 *
 * Twelve swatches; the ones already taken by somebody else are still
 * selectable (she may genuinely want two people on the same shade) but are
 * marked, so a clash is a choice rather than an accident like the last one.
 */
function StaffColorPicker({
  name, value, onPick,
}: { name: string; value: string | null; onPick: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <span className="relative inline-block" ref={ref as React.RefObject<HTMLDivElement>}>
      <button
        type="button"
        data-staff-color
        onClick={() => setOpen((o) => !o)}
        title={value ? `Colour: ${value}` : "Pick a colour"}
        aria-label={`Colour for ${name}`}
        className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
      >
        <Avatar name={name} color={value} size="xs" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 grid w-40 grid-cols-6 gap-1 rounded-xl border border-zinc-800 bg-zinc-950 p-2 shadow-xl shadow-black/40">
          {STAFF_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              onClick={() => { onPick(c); setOpen(false); }}
              className={cn(
                "h-5 w-5 rounded-full ring-offset-2 ring-offset-zinc-950",
                `staff-${c}`,
                value === c && "ring-2 ring-white",
              )}
            />
          ))}
        </div>
      )}
    </span>
  );
}
